import { useState, useEffect, useCallback } from "react";
import { FetchSelectedUserApi, viewEmployee } from "../services/userService";
import { useNavigate } from "react-router-dom";
import {
  validateEmail,
  validatePhone,
  validateRequired,
} from "../utils/validators";
import { addUser } from "../services/userService";
import { updateEmployeeStatusApi } from "../services/userService";
import { editUser } from "../services/userService";

export const useEmployeeList = (
  companyId,
  search,
  currentPage,
  perPage,
  status
) => {
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [totalEmpCount, setTotalEmpCount] = useState();

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await viewEmployee(
        `&company_id=${companyId}&search=${search}&page=${currentPage}&page_count=${perPage}&active_status=${status}`
      );
      setEmployees(response.get_employee_details);
      setTotalCount(response.page_context.total);
      setTotalEmpCount(response.employee_count);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [companyId, search, currentPage, perPage, status]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEmployees();
    }, 500); // 500ms delay

    // Clean up the timer if the dependencies change before the delay is over
    return () => clearTimeout(timer);
  }, [fetchEmployees]);

  return {
    employees,
    totalCount,
    loading,
    totalEmpCount,
    refresh: fetchEmployees,
  };
};

//Add user

export const useAddEmployee = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    company_id: [],
  });

  const validateStep = () => {
    const errors = {};
    const data = formData || {}; // Ensure data exists

    if (!validateRequired(data.first_name || "")) {
      errors.first_name = "First name is required";
    }
    // if (!validateRequired(data.last_name || "")) {
    //   errors.last_name = "Last name is required";
    // }
    if (!validateEmail(data.email || "")) {
      errors.email = "Invalid email address";
    }
    if (!validatePhone(data.phone || "")) {
      errors.phone = "Invalid phone number";
    }

    if (!validateRequired(data.address || "")) {
      errors.address = "Address is required";
    }
    if (!data.company_id || data.company_id.length === 0) {
      errors.company_id = "Company is required";
    }

    setError(errors);
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (name === "email" && validateEmail(value)) {
        delete updatedErrors.email;
      } else if (name === "phone" && validatePhone(value)) {
        delete updatedErrors.phone;
      } else if (validateRequired(value)) {
        delete updatedErrors[name];
      }

      return updatedErrors;
    });
  };
  const handleSubmit = async () => {
    setLoading(true);
    const errors = validateStep();
    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      await addUser(formData);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address: "",
        company_id: [],
      });
      setError({});
      navigate("/Userlist", { replace: true });
    } catch (error) {
      setError(error.data);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    error,
    handleInputChange,
    handleSubmit,
  };
};

//Edit

export const useEditEmployee = (onClose) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    row_id: "", // Include the ID
    phone: "",
    address: "",
    company_id: [],
    id: "",
  });

  const validateStep = () => {
    const errors = {};
    const data = formData || {}; // Ensure data exists

    if (!validateRequired(data.first_name || "")) {
      errors.first_name = "First name is required";
    }
    // if (!validateRequired(data.last_name || "")) {
    //   errors.last_name = "Last name is required";
    // }
    if (!validateEmail(data.email || ""))
      errors.email = "Invalid email address";
    if (!validatePhone(data.phone || "")) errors.phone = "Invalid phone number";

    if (!validateRequired(data.address || "")) {
      errors.address = "Address is required";
    }
    if (!data.company_id || data.company_id.length === 0) {
      errors.company_id = "Company is required";
    }
    setError(errors);
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (name === "email" && validateEmail(value)) {
        delete updatedErrors.email;
      } else if (name === "phone" && validatePhone(value)) {
        delete updatedErrors.phone;
      } else if (validateRequired(value)) {
        delete updatedErrors[name];
      }

      return updatedErrors;
    });
  };
  const handleSubmit = async () => {
    setLoading(true);
    const errors = validateStep();
    if (Object.keys(errors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      await editUser(formData);
      onClose(); // Close the popup on success

      //   setFormData({
      //     row_id:  "", // Include the ID
      //     first_name: "",
      //     last_name: "",
      //     email: "",
      //     phone: "",
      //     address: "",
      //     company_id: [],
      //   });

      setError({});
      // navigate("/Userlist", { replace: true });
    } catch (error) {
      setError(error.data);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    error,
    handleInputChange,
    handleSubmit,
    setFormData, // Add this to the returned object
  };
};

//Status Update

export const useUpdateEmployeeStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateEmployeeStatus = async (ids, statusType) => {
    setLoading(true);
    setError(null);
    const data = {
      active_status: statusType,
      ids: ids,
    };

    try {
      await updateEmployeeStatusApi(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return { updateEmployeeStatus, loading, error };
};

//Fetch Selected User

export const useFetchSelectedPerson = (id) => {
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState([]);

  const fetchUser = useCallback(async () => {
    setLoading(true);
    try {
      const response = await FetchSelectedUserApi(`?employee_id=${id}`);
      setSelectedUser(response.selected_employee_details);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    loading,
    selectedUser,
    refresh: fetchUser,
  };
};
